import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useMutation, useQuery} from '@apollo/client/react';
import {AlertCircle, CreditCard, Loader2, Plus, Trash2, X} from 'lucide-react';
import {
  ActiveSubscription,
  CwayModule,
  SeatAssignment,
  SeatType,
  SeatUsage,
  SubscriptionAddon,
  SubscriptionPrice,
  SubscriptionTier,
  SubscriptionValidation,
} from '../../types/graphql';
import {SeatType as SeatTypeInput} from 'types/__generated__/graphql';
import type {User} from 'types/__generated__/graphql';
import {Pill, SeatMeter, SubscriptionStatusPills} from '../SubscriptionBadges';
import {SubscriptionOffers} from '../SubscriptionOffers';
import {
  DEFAULT_SEAT_TYPE,
  describeProductLine,
  formatDate,
  formatLimit,
  formatMonthlyTotals,
  formatMoney,
  isCwayTrial,
  isInvoiced,
  isSeatProduct,
  LIGHTBOX_SEAT_TYPE,
  monthlyTotals,
  PAST_DUE_STATUS,
  seatNoun,
  subscriptionStatusLabel,
  todayLocalISO,
  toEpochEndOfDay,
  UNLIMITED,
  UNPAID_STATUS,
} from '../../utils/subscriptions';
import {gql} from 'types/__generated__';

export const SUBSCRIPTION_DETAIL_QUERY = gql(`
  query OrganisationSubscriptionDetail($id: UUID) {
    organisation(id: $id) {
      id
      name
      activeSubscription {
        id
        status
        isActive
        isTrial
        isCwayTrial
        autoRenewal
        currentPeriodStart
        currentPeriodEnd
        hasModulePricing
        isManaged
        paymentTermsDays
        entitlements {
          module
          tier
          quantity
          includedUsers
          storageQuotaGb
          trafficQuotaGb
          skuLimit
        }
        products {
          id
          name
          description
          features
          quantity
          priceId
          module
          tier
          addon
          seatType
          price {
            amount
            currency
          }
        }
        createdBy {
          id
          firstName
          lastName
          email
        }
      }
      seatUsage(seatType: PROJECT) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
      lightboxSeatUsage: seatUsage(seatType: LIGHTBOX) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
      seatAssignments {
        userId
        seatType
      }
      requiresSeats
      users {
        id
        firstName
        lastName
        email
      }
    }
  }
`);

export const SUBSCRIPTION_PRODUCTS_QUERY = gql(`
  query SubscriptionProductsCatalog {
    subscriptionProducts {
      id
      name
      module
      tier
      addon
      seatType
      price {
        amount
        currency
      }
    }
  }
`);

const START_TRIAL = gql(`
  mutation StartOrganisationTrial($organisationId: UUID!, $trialEnd: Long!, $reason: String) {
    startOrganisationTrial(organisationId: $organisationId, trialEnd: $trialEnd, reason: $reason)
  }
`);

const UPDATE_TRIAL = gql(`
  mutation UpdateOrganisationTrial($organisationId: UUID!, $trialEnd: Long!, $reason: String) {
    updateOrganisationTrial(organisationId: $organisationId, trialEnd: $trialEnd, reason: $reason)
  }
`);

const STOP_TRIAL = gql(`
  mutation StopOrganisationTrial($organisationId: UUID!, $reason: String) {
    stopOrganisationTrial(organisationId: $organisationId, reason: $reason)
  }
`);

export const ASSIGN_SEAT = gql(`
  mutation AssignSeat($userId: UUID!, $seatType: SeatType!, $organisationId: UUID) {
    assignSeat(userId: $userId, seatType: $seatType, organisationId: $organisationId)
  }
`);

const REVOKE_SEAT = gql(`
  mutation RevokeSeat($userId: UUID!, $seatType: SeatType!, $organisationId: UUID) {
    revokeSeat(userId: $userId, seatType: $seatType, organisationId: $organisationId)
  }
`);

const SET_SUBSCRIPTION_SEATS = gql(`
  mutation SetSubscriptionSeats($quantity: Long!, $organisationId: UUID) {
    setSubscriptionSeats(quantity: $quantity, organisationId: $organisationId)
  }
`);

const UPDATE_SUBSCRIPTION_ITEMS = gql(`
  mutation UpdateSubscriptionItems($items: [SubscriptionItemInput!]!, $organisationId: UUID) {
    updateSubscriptionItems(items: $items, organisationId: $organisationId)
  }
`);

export const VALIDATE_SUBSCRIPTIONS = gql(`
  query ValidateSubscriptions($organisationId: UUID) {
    validateSubscriptions(organisationId: $organisationId) {
      subscriptionId
      status
      isActive
      isCwayTrial
      existsInStripe
      orphaned
    }
  }
`);

const REMOVE_ORPHANED_SUBSCRIPTION = gql(`
  mutation RemoveOrphanedSubscription($subscriptionId: String!, $organisationId: UUID) {
    removeOrphanedSubscription(subscriptionId: $subscriptionId, organisationId: $organisationId)
  }
`);

/**
 * Scheduled cancel: Stripe keeps the subscription running until the period ends, then closes
 * it. Reaches a specific subscription (a past-due one that is not the active one) by id.
 */
export const CANCEL_SUBSCRIPTION = gql(`
  mutation CancelSubscription($subscriptionId: String, $organisationId: UUID) {
    cancelSubscription(subscriptionId: $subscriptionId, organisationId: $organisationId)
  }
`);

/** Hard cancel: ends the Stripe subscription now, with no final invoice and no proration. */
export const CANCEL_SUBSCRIPTION_IMMEDIATELY = gql(`
  mutation CancelSubscriptionImmediately($subscriptionId: String!, $organisationId: UUID!, $reason: String) {
    cancelSubscriptionImmediately(subscriptionId: $subscriptionId, organisationId: $organisationId, reason: $reason)
  }
`);

/** Mutation variables take the generated enum; the rest of the UI speaks the domain union. */
const SEAT_TYPE_INPUTS: Record<SeatType, SeatTypeInput> = {
  PROJECT: SeatTypeInput.Project,
  LIGHTBOX: SeatTypeInput.Lightbox,
};

interface DetailUser extends Pick<User, 'id' | 'firstName' | 'lastName' | 'email'> {}

interface SubscriptionDetail extends ActiveSubscription {
  createdBy?: DetailUser;
}

interface OrganisationDetail {
  id: string;
  name: string;
  activeSubscription: SubscriptionDetail | null;
  seatUsage: SeatUsage | null;
  /** Never null from the server: all zeros when the organisation pays for no Lightbox seats. */
  lightboxSeatUsage: SeatUsage | null;
  seatAssignments: SeatAssignment[];
  requiresSeats: boolean;
  users: DetailUser[];
}

interface SubscriptionDetailResult {
  organisation: OrganisationDetail | null;
}

interface CatalogProduct {
  id: string;
  name: string;
  module?: CwayModule | null;
  tier?: SubscriptionTier | null;
  addon?: SubscriptionAddon | null;
  /** Set on the seat product, which the item editor refuses — seats go through the seat editor. */
  seatType?: SeatType | null;
  price: SubscriptionPrice;
}

interface ItemDraft {
  /** Identity within the draft — a product id alone is not unique across items. */
  key: string;
  productId: string;
  priceId?: string | null;
  label: string;
  quantity: number;
}

interface SubscriptionDetailModalProps {
  organisationId: string;
  organisationName: string;
  onClose: () => void;
  onChanged: () => void;
}

function defaultTrialEndDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Two subscription items can sit on the same product with different negotiated prices. */
function itemKey(productId: string, priceId?: string | null): string {
  return `${productId}::${priceId || ''}`;
}

const inputClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100';
const primaryButtonClass =
  'px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors';
const dangerButtonClass =
  'px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors';
const rowActionClass = 'text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50';
const outlineDangerButtonClass =
  'px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors';
const subtleButtonClass =
  'px-3 py-1.5 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors';

/** Inline confirm bar shown before destructive / billing-sensitive actions. */
function ConfirmRow({
  message,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  message: React.ReactNode;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
      <div className="flex-1 min-w-[200px]">{message}</div>
      <button onClick={onConfirm} disabled={busy} className={dangerButtonClass}>
        {busy ? 'Working…' : confirmLabel}
      </button>
      <button onClick={onCancel} disabled={busy} className={subtleButtonClass}>
        Cancel
      </button>
    </div>
  );
}

type PendingConfirm =
  | {kind: 'assignSeat'; user: DetailUser; seatType: SeatType}
  | {kind: 'revokeSeat'; user: DetailUser; seatType: SeatType}
  | {kind: 'setSeats'; quantity: number}
  | {kind: 'updateItems'; items: ItemDraft[]}
  | {kind: 'removeOrphan'; subscriptionId: string}
  | {kind: 'cancelAtPeriodEnd'; subscriptionId: string; isActive: boolean}
  | {kind: 'cancelNow'; subscriptionId: string; isActive: boolean};

/** The members holding one kind of seat, each with a revoke, and a picker to assign one more. */
function SeatHolders({
  seatType,
  holders,
  candidates,
  selectedUserId,
  onSelect,
  onPending,
  disabled,
  exhaustedHint,
}: {
  seatType: SeatType;
  holders: DetailUser[];
  candidates: DetailUser[];
  selectedUserId: string;
  onSelect: (userId: string) => void;
  onPending: (pending: PendingConfirm) => void;
  disabled: boolean;
  /** Set when the block is full: assigning is disabled and this says why. */
  exhaustedHint: string | null;
}) {
  return (
    <div className="space-y-3">
      {holders.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No member holds a {seatNoun(seatType)}.</p>}

      {holders.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {holders.map((user) => (
                <tr key={user.id}>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                    {user.firstName} {user.lastName}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{user.email}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onPending({kind: 'revokeSeat', user, seatType})}
                      disabled={disabled}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                    >
                      Revoke {seatNoun(seatType)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {candidates.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-[220px]">
            Assign a {seatNoun(seatType)} to a member
            <select value={selectedUserId} onChange={(e) => onSelect(e.target.value)} className={inputClass}>
              <option value="">Select a member…</option>
              {candidates.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => {
              const user = candidates.find((candidate) => candidate.id === selectedUserId);
              if (user) onPending({kind: 'assignSeat', user, seatType});
            }}
            disabled={disabled || !selectedUserId || !!exhaustedHint}
            className={primaryButtonClass}
          >
            Assign {seatNoun(seatType)}
          </button>
          {exhaustedHint && <span className="text-xs text-amber-700 dark:text-amber-400 basis-full">{exhaustedHint}</span>}
        </div>
      )}
    </div>
  );
}

const CANCELED_STATUS = 'CANCELED';

/** Whether the health table should offer cancel actions on this row. */
function isCancellable(row: SubscriptionValidation): boolean {
  return row.existsInStripe && !row.orphaned && !row.isCwayTrial && row.status.toUpperCase() !== CANCELED_STATUS;
}

export default function SubscriptionDetailModal({organisationId, organisationName, onClose, onChanged}: SubscriptionDetailModalProps) {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [trialEndInput, setTrialEndInput] = useState(defaultTrialEndDate);
  const [trialReason, setTrialReason] = useState('');
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [seatsInput, setSeatsInput] = useState<number | null>(null);
  const [itemsDraft, setItemsDraft] = useState<ItemDraft[] | null>(null);
  const [addItemProductId, setAddItemProductId] = useState('');
  const [addItemCustomId, setAddItemCustomId] = useState('');
  const [addItemQuantity, setAddItemQuantity] = useState(1);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const pollGeneration = useRef(0);

  const {loading, error: queryError, data, refetch} = useQuery<SubscriptionDetailResult>(SUBSCRIPTION_DETAIL_QUERY, {
    variables: {id: organisationId},
    fetchPolicy: 'cache-and-network',
  });

  const org = data?.organisation;
  const subscription: SubscriptionDetail | null = org?.activeSubscription || null;
  const seatUsage: SeatUsage | null = org?.seatUsage || null;
  const lightboxSeatUsage: SeatUsage | null = org?.lightboxSeatUsage || null;
  const hasActiveSubscription = !!subscription && subscription.isActive;
  const cwayTrial = hasActiveSubscription && isCwayTrial(subscription);
  const modulePricing = hasActiveSubscription && subscription!.hasModulePricing;
  // Trials snapshot one display-only product ("CWAY Trial"); trials from before that
  // change have none, so everything rendered from it needs the fallback below.
  const trialDisplayProduct = cwayTrial ? subscription!.products[0] ?? null : null;

  // Catalog powers the item editor's add row
  const {data: catalogData} = useQuery<{subscriptionProducts: CatalogProduct[]}>(SUBSCRIPTION_PRODUCTS_QUERY, {skip: loading});
  const catalog = catalogData?.subscriptionProducts || [];

  const [startTrial, {loading: starting}] = useMutation(START_TRIAL);
  const [updateTrial, {loading: updating}] = useMutation(UPDATE_TRIAL);
  const [stopTrial, {loading: stopping}] = useMutation(STOP_TRIAL);
  const [assignSeat] = useMutation(ASSIGN_SEAT);
  const [revokeSeat] = useMutation(REVOKE_SEAT);
  const [setSubscriptionSeats] = useMutation(SET_SUBSCRIPTION_SEATS);
  const [updateSubscriptionItems] = useMutation(UPDATE_SUBSCRIPTION_ITEMS);
  const [removeOrphanedSubscription] = useMutation(REMOVE_ORPHANED_SUBSCRIPTION);
  const [cancelSubscription] = useMutation(CANCEL_SUBSCRIPTION);
  const [cancelSubscriptionImmediately] = useMutation(CANCEL_SUBSCRIPTION_IMMEDIATELY);

  /**
   * Staff-only health check: which stored subscriptions Stripe still knows about. A failure
   * here is shown as a failure — an errored check is not evidence that anything is an orphan.
   */
  const {
    data: validationData,
    error: validationError,
    loading: validationLoading,
    refetch: refetchValidation,
  } = useQuery<{validateSubscriptions: SubscriptionValidation[]}>(VALIDATE_SUBSCRIPTIONS, {
    variables: {organisationId},
    fetchPolicy: 'cache-and-network',
  });
  const validations = validationData?.validateSubscriptions || [];
  const orphans = validations.filter((row) => row.orphaned);
  const [actionBusy, setActionBusy] = useState(false);
  const mutating = starting || updating || stopping || actionBusy || syncing;

  /** Holders and candidates per seat kind: a member can hold a project seat and a Lightbox seat independently. */
  const seatsByKind = useMemo(() => {
    const byKind = (seatType: SeatType) => {
      const users = org?.users || [];
      const heldBy = new Set((org?.seatAssignments || []).filter((a) => a.seatType === seatType).map((a) => a.userId));
      return {
        holders: users.filter((user) => heldBy.has(user.id)),
        candidates: users.filter((user) => !heldBy.has(user.id)),
      };
    };
    return {PROJECT: byKind('PROJECT'), LIGHTBOX: byKind('LIGHTBOX')} satisfies Record<SeatType, unknown>;
  }, [org]);
  const [assignSeatUserIds, setAssignSeatUserIds] = useState<Record<SeatType, string>>({PROJECT: '', LIGHTBOX: ''});

  // Closing the modal must stop an in-flight poll; without this it keeps refetching for
  // up to half a minute against a component nobody is looking at any more.
  useEffect(() => () => void pollGeneration.current++, []);

  /**
   * Billing mutations against Stripe only materialise when the webhook lands, so poll-refetch
   * until the *expected* state is visible (2s interval, ~30s timeout). Waiting on the specific
   * outcome rather than "anything changed" keeps a concurrent edit — a member taking a seat,
   * another admin saving — from being mistaken for confirmation, and lets a change that was
   * already in effect settle at once instead of timing out with a false error.
   */
  const pollUntilSettled = async (isSettled: (organisation: OrganisationDetail | null | undefined) => boolean) => {
    const generation = ++pollGeneration.current;
    if (isSettled(org)) return;
    setSyncing(true);
    try {
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (pollGeneration.current !== generation) return;
        let latest: OrganisationDetail | null | undefined;
        try {
          latest = (await refetch()).data?.organisation;
        } catch {
          continue; // a dropped poll request says nothing about the mutation
        }
        if (pollGeneration.current !== generation) return;
        if (isSettled(latest)) return;
      }
      setError('The change was requested but Stripe has not confirmed it yet — refresh in a moment.');
    } finally {
      if (pollGeneration.current === generation) setSyncing(false);
    }
  };

  const runAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
    options?: {settled?: (organisation: OrganisationDetail | null | undefined) => boolean},
  ): Promise<boolean> => {
    setError('');
    setSuccess('');
    setActionBusy(true);
    try {
      await action();
    } catch (err: any) {
      setError(err.message || 'The request failed');
      setActionBusy(false);
      return false;
    }

    // Past this point the mutation applied. A refresh that fails afterwards is a stale
    // view, never a reason to report the billing change as failed and invite a re-run.
    setPending(null);
    setShowStopConfirm(false);
    setSuccess(successMessage);
    try {
      if (options?.settled) {
        await pollUntilSettled(options.settled);
      } else {
        await refetch();
      }
    } catch {
      setSuccess(`${successMessage}. The view could not be refreshed — reopen to see the current state.`);
    } finally {
      setActionBusy(false);
    }
    onChanged();
    return true;
  };

  const handleStartTrial = () =>
    runAction(
      () =>
        startTrial({
          variables: {
            organisationId,
            trialEnd: toEpochEndOfDay(trialEndInput),
            reason: trialReason.trim() || null,
          },
        }),
      'Trial started',
    );

  const handleUpdateTrial = () =>
    runAction(
      () => updateTrial({variables: {organisationId, trialEnd: toEpochEndOfDay(trialEndInput), reason: trialReason.trim() || null}}),
      'Trial end date updated',
    );

  const handleStopTrial = () => runAction(() => stopTrial({variables: {organisationId, reason: trialReason.trim() || null}}), 'Trial stopped');

  const confirmPending = () => {
    if (!pending) return;
    switch (pending.kind) {
      case 'assignSeat': {
        const {user, seatType} = pending;
        return runAction(
          () => assignSeat({variables: {userId: user.id, seatType: SEAT_TYPE_INPUTS[seatType], organisationId}}),
          `${seatNoun(seatType, {capital: true})} assigned to ${user.email}`,
        ).then((ok) => ok && setAssignSeatUserIds((ids) => ({...ids, [seatType]: ''})));
      }
      case 'revokeSeat':
        return runAction(
          () => revokeSeat({variables: {userId: pending.user.id, seatType: SEAT_TYPE_INPUTS[pending.seatType], organisationId}}),
          `${seatNoun(pending.seatType, {capital: true})} freed for ${pending.user.email}`,
        );
      case 'setSeats': {
        const expected = pending.quantity;
        return runAction(
          () => setSubscriptionSeats({variables: {quantity: expected, organisationId}}),
          'Seat change requested',
          {settled: (organisation) => organisation?.seatUsage?.purchasedSeats === expected},
        ).then((ok) => ok && setSeatsInput(null));
      }
      case 'removeOrphan':
        return runAction(
          () => removeOrphanedSubscription({variables: {subscriptionId: pending.subscriptionId, organisationId}}),
          `Removed orphaned subscription ${pending.subscriptionId}`,
        ).then((ok) => {
          if (ok) refetchValidation();
          return ok;
        });
      case 'cancelAtPeriodEnd': {
        const {subscriptionId, isActive} = pending;
        return runAction(
          () => cancelSubscription({variables: {subscriptionId, organisationId}}),
          `Subscription ${subscriptionId} cancels at the end of the current period`,
          // The scheduled cancel lands through the webhook as autoRenewal=false. A subscription
          // that is not the active one is not in this view at all, so there is nothing to wait for.
          isActive
            ? {
                settled: (organisation) => {
                  const live = organisation?.activeSubscription;
                  return !live || live.id !== subscriptionId || !live.autoRenewal;
                },
              }
            : undefined,
        ).then((ok) => {
          if (ok) refetchValidation();
          return ok;
        });
      }
      case 'cancelNow': {
        const {subscriptionId, isActive} = pending;
        return runAction(
          () => cancelSubscriptionImmediately({variables: {subscriptionId, organisationId, reason: cancelReason.trim() || null}}),
          `Subscription ${subscriptionId} cancelled`,
          isActive
            ? {
                settled: (organisation) => {
                  const live = organisation?.activeSubscription;
                  return !live || live.id !== subscriptionId || !live.isActive;
                },
              }
            : undefined,
        ).then((ok) => {
          if (ok) {
            setCancelReason('');
            refetchValidation();
          }
          return ok;
        });
      }
      case 'updateItems': {
        const expected = new Set(pending.items.map((item) => item.productId));
        return runAction(
          () =>
            updateSubscriptionItems({
              variables: {
                items: pending.items.map((item) => ({productId: item.productId, quantity: item.quantity})),
                organisationId,
              },
            }),
          'Subscription items update requested',
          {
            settled: (organisation) => {
              const live = organisation?.activeSubscription?.products.filter((product) => !isSeatProduct(product));
              if (!live) return false;
              const liveIds = new Set(live.map((product) => product.id));
              return liveIds.size === expected.size && [...expected].every((id) => liveIds.has(id));
            },
          },
        ).then((ok) => ok && setItemsDraft(null));
      }
    }
  };

  const pendingMessage = (p: PendingConfirm): React.ReactNode => {
    switch (p.kind) {
      case 'assignSeat':
        return (
          <>
            Assign a {seatNoun(p.seatType)} to <b>{p.user.email}</b>? They start consuming one of the organisation&apos;s{' '}
            {seatNoun(p.seatType, {plural: true})}.
          </>
        );
      case 'revokeSeat':
        return (
          <>
            Free the {seatNoun(p.seatType)} of <b>{p.user.email}</b>? The purchased seat stays on the subscription and can be reassigned.
          </>
        );
      case 'setSeats': {
        const increasing = p.quantity > (seatUsage?.purchasedSeats ?? 0);
        return increasing ? (
          <>
            Set purchased extra {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})} to <b>{p.quantity}</b>? The added seats are invoiced
            immediately, prorated for the rest of the period, and become usable once Stripe confirms.
          </>
        ) : (
          <>
            Set purchased extra {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})} to <b>{p.quantity}</b>? Seats decrease immediately; unused
            time is credited to the next invoice — no card refund.
          </>
        );
      }
      case 'removeOrphan':
        return (
          <>
            Remove <span className="font-mono">{p.subscriptionId}</span> from this organisation? Stripe has no record of it, so this is
            a <b>data correction, not a cancellation</b> — nothing is cancelled or refunded, and the organisation stops believing it has
            this subscription.
          </>
        );
      case 'cancelAtPeriodEnd':
        return (
          <>
            Cancel <span className="font-mono">{p.subscriptionId}</span> at the end of the current period? The customer keeps access and is not billed
            again; nothing is refunded. Stripe closes the subscription when the period ends.
          </>
        );
      case 'cancelNow':
        return (
          <div className="space-y-2">
            <p>
              Cancel <span className="font-mono">{p.subscriptionId}</span> <b>right now</b>? Access ends immediately, with{' '}
              <b>no final invoice and no proration</b> — unused time is neither invoiced nor credited. This cannot be undone.
            </p>
            <label className="block text-sm">
              Reason <span className="text-amber-700/70 dark:text-amber-300/70">(stored on the Stripe subscription)</span>
              <input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Customer churned, duplicate subscription"
                className={inputClass}
              />
            </label>
          </div>
        );
      case 'updateItems':
        return (
          <>
            Replace the subscription items with: <b>{p.items.map((item) => item.label).join(', ') || 'none'}</b>? Anything omitted is
            removed (prorated). The purchased-seat item is preserved automatically.
            {droppedNegotiatedItems.length > 0 && (
              <>
                {' '}
                <b>
                  {droppedNegotiatedItems.join(', ')} {droppedNegotiatedItems.length === 1 ? 'sits' : 'sit'} on a negotiated price
                </b>{' '}
                — removing {droppedNegotiatedItems.length === 1 ? 'it' : 'them'} loses that price, and re-adding bills the product&apos;s
                default price instead.
              </>
            )}
          </>
        );
    }
  };

  /**
   * Seeds the draft with every non-seat item — including ones with no module. The server
   * reconciles to exactly what we send, so a managed/custom product left out here would be
   * silently cancelled the moment the admin pressed Apply.
   */
  const beginEditItems = () => {
    if (!subscription) return;
    setError('');
    setItemsDraft(
      subscription.products
        .filter((product) => !isSeatProduct(product))
        .map((product) => ({
          key: itemKey(product.id, product.priceId),
          productId: product.id,
          priceId: product.priceId,
          label: product.name,
          quantity: product.quantity,
        })),
    );
  };

  const addDraftItem = () => {
    const customId = addItemCustomId.trim();
    const productId = customId || addItemProductId;
    if (!productId || !itemsDraft) return;
    if (itemsDraft.some((item) => item.productId === productId)) {
      setError('That product is already in the list — the server rejects duplicate products');
      return;
    }
    const catalogProduct = catalog.find((product) => product.id === productId);
    if (isSeatProduct(catalogProduct)) {
      setError(`${seatNoun(DEFAULT_SEAT_TYPE, {capital: true, plural: true})} are changed with the seat editor, not as a module item`);
      return;
    }
    setError('');
    setItemsDraft([
      ...itemsDraft,
      {
        key: itemKey(productId, null),
        productId,
        priceId: null,
        label: catalogProduct?.name || productId,
        quantity: Math.max(1, addItemQuantity),
      },
    ]);
    setAddItemProductId('');
    setAddItemCustomId('');
    setAddItemQuantity(1);
  };

  // The server rejects a list with the same product twice, which an existing subscription
  // carrying two negotiated prices for one product would otherwise walk straight into.
  const duplicateDraftProducts = useMemo(() => {
    if (!itemsDraft) return [];
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const item of itemsDraft) {
      if (seen.has(item.productId)) duplicates.add(item.label);
      seen.add(item.productId);
    }
    return [...duplicates];
  }, [itemsDraft]);

  /** Items dropped from the draft that were sitting on their own (negotiated) price. */
  const droppedNegotiatedItems = useMemo(() => {
    if (!itemsDraft || !subscription) return [];
    const keptKeys = new Set(itemsDraft.map((item) => item.key));
    return subscription.products
      .filter((product) => !isSeatProduct(product) && product.priceId && !keptKeys.has(itemKey(product.id, product.priceId)))
      .map((product) => product.name);
  }, [itemsDraft, subscription]);

  const seatFloor = seatUsage ? Math.max(0, seatUsage.usedSeats - seatUsage.includedSeats) : 0;
  const canEditSeats =
    modulePricing && !subscription!.isTrial && !!seatUsage && seatUsage.totalSeats !== UNLIMITED && seatUsage.includedSeats !== UNLIMITED;
  const monthly = hasActiveSubscription && !subscription!.isTrial ? formatMonthlyTotals(monthlyTotals(subscription!.products)) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header — sticky, and it carries the feedback strip: a confirm question or a result must stay in
              view no matter how far down the dialog the button that raised it sits. */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <CreditCard size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{organisationName}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Subscription &amp; seats</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {syncing && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Waiting for Stripe…
                  </span>
                )}
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <X size={20} className="text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
            {(error || queryError || success || pending) && (
              <div className="px-6 pb-4 space-y-3">
                {(error || queryError) && (
                  <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    {error || queryError?.message}
                  </div>
                )}
                {success && !error && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
                    {success}
                  </div>
                )}
                {pending && (
                  <ConfirmRow
                    message={pendingMessage(pending)}
                    confirmLabel="Confirm"
                    busy={actionBusy}
                    onConfirm={confirmPending}
                    onCancel={() => setPending(null)}
                  />
                )}
              </div>
            )}
          </div>

          <div className="p-6 space-y-6">
            {loading && !org && <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>}

            {org && (
              <>
                {/* Subscription overview */}
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Subscription</h3>
                  {hasActiveSubscription ? (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <SubscriptionStatusPills subscription={subscription} />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Period start</p>
                          <p className="text-gray-900 dark:text-gray-100">{formatDate(subscription!.currentPeriodStart)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{subscription!.isTrial ? 'Trial ends' : 'Period ends'}</p>
                          <p className="text-gray-900 dark:text-gray-100">{formatDate(subscription!.currentPeriodEnd)}</p>
                        </div>
                        {monthly && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Monthly total</p>
                            <p className="text-gray-900 dark:text-gray-100">{monthly}</p>
                          </div>
                        )}
                        {subscription!.createdBy && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Created by</p>
                            <p className="text-gray-900 dark:text-gray-100 truncate" title={subscription!.createdBy.email}>
                              {subscription!.createdBy.firstName} {subscription!.createdBy.lastName}
                            </p>
                          </div>
                        )}
                      </div>
                      {isInvoiced(subscription) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Stripe sends each invoice to the customer, payable within {subscription!.paymentTermsDays} days by bank transfer or card
                          on the hosted invoice. There is no card to replace.
                          {subscription!.status.toUpperCase() === PAST_DUE_STATUS &&
                            ' The latest invoice is past its due date — Stripe keeps sending reminders, and access continues until it gives up' +
                              ' and marks the subscription unpaid.'}
                        </p>
                      )}
                      <p className="text-xs font-mono text-gray-400 dark:text-gray-500 break-all">{subscription!.id}</p>
                      {/* Cway trials end with Stop trial below; only Stripe-backed subscriptions cancel here. */}
                      {!cwayTrial && (
                        <div className="flex flex-wrap items-center gap-3 pt-1">
                          {subscription!.autoRenewal ? (
                            <button
                              onClick={() => setPending({kind: 'cancelAtPeriodEnd', subscriptionId: subscription!.id, isActive: true})}
                              disabled={mutating}
                              className={outlineDangerButtonClass}
                            >
                              Cancel at period end
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              Already set to cancel on {formatDate(subscription!.currentPeriodEnd)}.
                            </span>
                          )}
                          <button
                            onClick={() => setPending({kind: 'cancelNow', subscriptionId: subscription!.id, isActive: true})}
                            disabled={mutating}
                            className={outlineDangerButtonClass}
                          >
                            Cancel immediately
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-300">
                      No active subscription
                      {subscription && ` (last status: ${subscriptionStatusLabel(subscription)})`}
                      {subscription && isInvoiced(subscription) && subscription.status.toUpperCase() === UNPAID_STATUS && (
                        <span className="block mt-1">
                          Stripe gave up collecting the overdue invoices, which is what ended access. Collection stays open: paying them in Stripe
                          brings the subscription back.
                        </span>
                      )}
                    </div>
                  )}
                </section>

                {/* Products / entitlements */}
                {hasActiveSubscription && subscription!.products.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Items</h3>
                      {!subscription!.isTrial && itemsDraft === null && (
                        <button onClick={beginEditItems} disabled={mutating} className={subtleButtonClass}>
                          {modulePricing ? 'Edit items' : 'Migrate to module pricing'}
                        </button>
                      )}
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="px-3 py-2 font-medium">Product</th>
                            <th className="px-3 py-2 font-medium">Kind</th>
                            <th className="px-3 py-2 font-medium">Quota</th>
                            <th className="px-3 py-2 font-medium text-right">Qty</th>
                            <th className="px-3 py-2 font-medium text-right">Unit / month</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                          {subscription!.products.map((product) => {
                            const entitlement = product.module
                              ? subscription!.entitlements.find((e) => e.module === product.module && e.tier === product.tier)
                              : undefined;
                            const quota = entitlement
                              ? [
                                  entitlement.includedUsers != null && `${formatLimit(entitlement.includedUsers)} users`,
                                  entitlement.storageQuotaGb != null && `${formatLimit(entitlement.storageQuotaGb, 'GB')} storage`,
                                  entitlement.trafficQuotaGb != null && `${formatLimit(entitlement.trafficQuotaGb, 'GB')} traffic`,
                                  entitlement.skuLimit != null && `${formatLimit(entitlement.skuLimit)} SKUs`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                              : '';
                            return (
                              <tr key={itemKey(product.id, product.priceId)}>
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                  {product.name}
                                  {product.priceId && (
                                    <span
                                      className="ml-2 text-xs text-gray-400 dark:text-gray-500"
                                      title={`On price ${product.priceId} — a negotiated price is lost if the item is removed and re-added`}
                                    >
                                      negotiated price
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                  {describeProductLine(product, subscription!)}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{quota || '—'}</td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right">{product.quantity}</td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-right whitespace-nowrap">
                                  {/* Trial products carry a display-only 0 price — a rendered $0 reads as a real price. */}
                                  {subscription!.isTrial ? '—' : formatMoney(product.price.amount, product.price.currency)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {itemsDraft !== null && (
                      <div className="mt-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          The subscription is reconciled to exactly this list — every item not listed here is removed (prorated), so
                          leave custom and managed products in place unless you mean to cancel them. Managed Enterprise products can be
                          added by Stripe product id.
                        </p>
                        {!modulePricing && (
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            This is a legacy single-product plan. Customers cannot change it through the module flow; applying a list of
                            module products here is the staff migration path. Remove the legacy line, add the modules the customer is
                            moving to, and set purchased {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})} with the seat editor afterwards.
                          </p>
                        )}
                        <ul className="space-y-1.5">
                          {itemsDraft.map((item) => (
                            <li
                              key={item.key}
                              className="flex items-center justify-between gap-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2"
                            >
                              <span className="text-gray-900 dark:text-gray-100">
                                {item.label}
                                {item.quantity > 1 && <span className="text-gray-500 dark:text-gray-400"> × {item.quantity}</span>}
                                <span className="ml-2 text-xs font-mono text-gray-400 dark:text-gray-500">{item.productId}</span>
                                {item.priceId && (
                                  <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">negotiated price</span>
                                )}
                              </span>
                              <button
                                onClick={() => setItemsDraft(itemsDraft.filter((other) => other.key !== item.key))}
                                disabled={mutating}
                                className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Remove item"
                              >
                                <Trash2 size={15} />
                              </button>
                            </li>
                          ))}
                          {itemsDraft.length === 0 && (
                            <li className="text-sm text-gray-500 dark:text-gray-400">No items — this would empty the plan.</li>
                          )}
                        </ul>
                        {duplicateDraftProducts.length > 0 && (
                          <p className="text-xs text-red-600 dark:text-red-400">
                            {duplicateDraftProducts.join(', ')} appears on more than one item. The server rejects duplicate products —
                            remove one of the rows, or change the prices in Stripe instead.
                          </p>
                        )}
                        <div className="flex flex-wrap items-end gap-2">
                          <label className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-[180px]">
                            Add from catalog
                            <select
                              value={addItemProductId}
                              onChange={(e) => setAddItemProductId(e.target.value)}
                              className={inputClass}
                            >
                              <option value="">Select a product…</option>
                              {catalog
                                .filter((product) => !isSeatProduct(product))
                                .map((product) => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} ({formatMoney(product.price.amount, product.price.currency)}/mo)
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-[160px]">
                            or Stripe product id
                            <input
                              type="text"
                              value={addItemCustomId}
                              onChange={(e) => setAddItemCustomId(e.target.value)}
                              placeholder="prod_…"
                              className={`${inputClass} font-mono`}
                            />
                          </label>
                          <label className="text-sm text-gray-700 dark:text-gray-300 w-20">
                            Qty
                            <input
                              type="number"
                              min={1}
                              value={addItemQuantity}
                              onChange={(e) => setAddItemQuantity(parseInt(e.target.value, 10) || 1)}
                              className={inputClass}
                            />
                          </label>
                          <button onClick={addDraftItem} disabled={mutating} className={`${subtleButtonClass} flex items-center gap-1`}>
                            <Plus size={14} /> Add
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPending({kind: 'updateItems', items: itemsDraft})}
                            disabled={mutating || itemsDraft.length === 0 || duplicateDraftProducts.length > 0}
                            className={primaryButtonClass}
                          >
                            Apply items
                          </button>
                          <button onClick={() => setItemsDraft(null)} disabled={mutating} className={subtleButtonClass}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {/* Seats */}
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                    {seatNoun(DEFAULT_SEAT_TYPE, {capital: true, plural: true})}
                  </h3>
                  {seatUsage ? (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                      <SeatMeter seatUsage={seatUsage} />

                      {canEditSeats && (
                        <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <label className="text-sm text-gray-700 dark:text-gray-300 w-40">
                            Purchased extra {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})}
                            <input
                              type="number"
                              min={seatFloor}
                              value={seatsInput ?? seatUsage.purchasedSeats}
                              onChange={(e) => setSeatsInput(Math.max(seatFloor, parseInt(e.target.value, 10) || 0))}
                              className={inputClass}
                            />
                          </label>
                          <button
                            onClick={() => setPending({kind: 'setSeats', quantity: seatsInput ?? seatUsage.purchasedSeats})}
                            disabled={mutating || seatsInput === null || seatsInput === seatUsage.purchasedSeats}
                            className={primaryButtonClass}
                          >
                            Set seats
                          </button>
                          <span className="text-xs text-gray-500 dark:text-gray-400 basis-full">
                            Floor {seatFloor} — {seatUsage.usedSeats} {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})} are in use and{' '}
                            {seatUsage.includedSeats} come with the tier;
                            to go lower, revoke seats first. Settled immediately: increases are invoiced prorated, decreases credit the
                            next invoice.
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Not on module-based pricing — no seat accounting for this organisation.
                    </p>
                  )}
                </section>

                {/* Seat holders */}
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                    {seatNoun(DEFAULT_SEAT_TYPE, {capital: true})} holders
                  </h3>
                  <SeatHolders
                    seatType={DEFAULT_SEAT_TYPE}
                    {...seatsByKind.PROJECT}
                    selectedUserId={assignSeatUserIds.PROJECT}
                    onSelect={(userId) => setAssignSeatUserIds((ids) => ({...ids, PROJECT: userId}))}
                    onPending={setPending}
                    disabled={mutating}
                    exhaustedHint={
                      org.requiresSeats && seatUsage?.seatsLeft === 0
                        ? `No ${seatNoun(DEFAULT_SEAT_TYPE, {plural: true})} left — buy more with the seat editor above, or free one first.`
                        : null
                    }
                  />
                </section>

                {/* Lightbox seats — only for an organisation that pays for some, or still has holders to revoke. */}
                {lightboxSeatUsage && (lightboxSeatUsage.totalSeats > 0 || seatsByKind.LIGHTBOX.holders.length > 0) && (
                  <section>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                      {seatNoun(LIGHTBOX_SEAT_TYPE, {capital: true, plural: true})}
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                        <SeatMeter seatUsage={lightboxSeatUsage} seatType={LIGHTBOX_SEAT_TYPE} />
                        {lightboxSeatUsage.totalSeats === 0 && (
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            None are paid for right now — typically a subscription awaiting payment — so the holders below have no Lightbox licence.
                            Their seats are kept until the subscription recovers or ends.
                          </p>
                        )}
                      </div>
                      <SeatHolders
                        seatType={LIGHTBOX_SEAT_TYPE}
                        {...seatsByKind.LIGHTBOX}
                        selectedUserId={assignSeatUserIds.LIGHTBOX}
                        onSelect={(userId) => setAssignSeatUserIds((ids) => ({...ids, LIGHTBOX: userId}))}
                        onPending={setPending}
                        disabled={mutating}
                        // Strict, unlike project seats: the server refuses every assignment past the paid block.
                        exhaustedHint={
                          lightboxSeatUsage.seatsLeft <= 0
                            ? `No ${seatNoun(LIGHTBOX_SEAT_TYPE, {plural: true})} left — Cway sales adds more on an offer or in Stripe, or free one first.`
                            : null
                        }
                      />
                    </div>
                  </section>
                )}

                {/* Trial management */}
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Trial management</h3>

                  {!hasActiveSubscription && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Start a Cway-managed trial (no Stripe subscription). The trial unlocks every module at the Enterprise tier and
                        presents itself to the customer as the &ldquo;CWAY Trial&rdquo; plan.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          Trial ends
                          <input
                            type="date"
                            value={trialEndInput}
                            min={todayLocalISO()}
                            onChange={(e) => setTrialEndInput(e.target.value)}
                            className={inputClass}
                          />
                        </label>
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          Reason
                          <input
                            type="text"
                            value={trialReason}
                            onChange={(e) => setTrialReason(e.target.value)}
                            placeholder="e.g. Sales trial agreed with …"
                            className={inputClass}
                          />
                        </label>
                      </div>
                      <button onClick={handleStartTrial} disabled={mutating || !trialEndInput} className={primaryButtonClass}>
                        {starting ? 'Starting…' : 'Start trial'}
                      </button>
                    </div>
                  )}

                  {cwayTrial && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Cway-managed trial ends <span className="font-semibold">{formatDate(subscription!.currentPeriodEnd)}</span>.
                      </p>
                      {trialDisplayProduct ? (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-1.5">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{trialDisplayProduct.name}</p>
                          {trialDisplayProduct.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-300">{trialDisplayProduct.description}</p>
                          )}
                          {(trialDisplayProduct.features?.length ?? 0) > 0 && (
                            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300">
                              {trialDisplayProduct.features!.map((feature) => (
                                <li key={feature}>{feature}</li>
                              ))}
                            </ul>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            This is what the customer sees as their plan. The copy lives on the Stripe product{' '}
                            <span className="font-mono">cway_trial</span> — edit it there, no deploy needed.
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Started before trial display info existed — the customer sees a plain &ldquo;Trial&rdquo; instead of the CWAY
                          Trial plan card.
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          New end date
                          <input
                            type="date"
                            value={trialEndInput}
                            min={todayLocalISO()}
                            onChange={(e) => setTrialEndInput(e.target.value)}
                            className={inputClass}
                          />
                        </label>
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          Reason
                          <input
                            type="text"
                            value={trialReason}
                            onChange={(e) => setTrialReason(e.target.value)}
                            placeholder="e.g. Extended after demo"
                            className={inputClass}
                          />
                        </label>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={handleUpdateTrial} disabled={mutating || !trialEndInput} className={primaryButtonClass}>
                          {updating ? 'Updating…' : 'Move end date'}
                        </button>
                        {showStopConfirm ? (
                          <span className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-300">Stop this trial now?</span>
                            <button onClick={handleStopTrial} disabled={mutating} className={dangerButtonClass}>
                              {stopping ? 'Stopping…' : 'Yes, stop it'}
                            </button>
                            <button onClick={() => setShowStopConfirm(false)} className={subtleButtonClass}>
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button onClick={() => setShowStopConfirm(true)} disabled={mutating} className={outlineDangerButtonClass}>
                            Stop trial
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {hasActiveSubscription && !cwayTrial && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {subscription!.isTrial
                        ? 'This is a Stripe trial — it converts or expires through Stripe, not from here.'
                        : 'This organisation has a live subscription; trials can only be started when there is none.'}
                    </p>
                  )}
                </section>

                {/* Sales offers — the same surface the organisation page carries, so a deal can be
                    built from the subscriptions list without a detour via the organisation page. */}
                <SubscriptionOffers organisationId={organisationId} organisationName={organisationName} headingLevel="h3" />

                {/* Subscription health — Stripe drift cleanup */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Subscription health</h3>
                    {validationLoading && <span className="text-xs text-gray-500 dark:text-gray-400">Checking Stripe…</span>}
                  </div>

                  {validationError ? (
                    <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>
                        Could not check subscriptions against Stripe: {validationError.message}. Nothing is being reported as orphaned —
                        retry before drawing conclusions.
                      </span>
                    </div>
                  ) : validations.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {validationLoading ? 'Checking…' : 'No stored subscriptions to check.'}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {orphans.length > 0 && (
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {orphans.length} subscription{orphans.length === 1 ? '' : 's'} exist{orphans.length === 1 ? 's' : ''} here but
                          not in Stripe — data drift from copying events between environments. Removing them is a local correction; it
                          cancels nothing.
                        </p>
                      )}
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
                            <tr>
                              <th className="px-3 py-2 font-medium">Subscription</th>
                              <th className="px-3 py-2 font-medium">Stored status</th>
                              <th className="px-3 py-2 font-medium">In Stripe</th>
                              <th className="px-3 py-2" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                            {validations.map((row) => (
                              <tr key={row.subscriptionId} className={row.orphaned ? 'bg-amber-50/60 dark:bg-amber-900/10' : undefined}>
                                <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-300 break-all">
                                  {row.subscriptionId}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-gray-600 dark:text-gray-300">{row.status.toLowerCase()}</span>
                                    {row.isCwayTrial && (
                                      <Pill tone="amber" square>
                                        Cway trial
                                      </Pill>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  {row.orphaned ? (
                                    <Pill tone="red" square>
                                      missing — orphaned
                                    </Pill>
                                  ) : row.existsInStripe ? (
                                    <Pill tone="green" square>
                                      yes
                                    </Pill>
                                  ) : (
                                    <Pill tone="gray" square>
                                      no Stripe backing
                                    </Pill>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {/* Removal is offered on orphaned rows and nowhere else. */}
                                  {row.orphaned && (
                                    <button
                                      onClick={() => setPending({kind: 'removeOrphan', subscriptionId: row.subscriptionId})}
                                      disabled={mutating}
                                      className={rowActionClass}
                                    >
                                      Remove orphan
                                    </button>
                                  )}
                                  {/* The active subscription has its cancel buttons in the overview; here the
                                      cancels reach the other Stripe-backed rows, e.g. a past-due one. */}
                                  {isCancellable(row) && !(hasActiveSubscription && row.subscriptionId === subscription!.id) && (
                                    <span className="inline-flex items-center gap-3">
                                      <button
                                        onClick={() =>
                                          setPending({kind: 'cancelAtPeriodEnd', subscriptionId: row.subscriptionId, isActive: row.isActive})
                                        }
                                        disabled={mutating}
                                        className={rowActionClass}
                                      >
                                        Cancel at period end
                                      </button>
                                      <button
                                        onClick={() => setPending({kind: 'cancelNow', subscriptionId: row.subscriptionId, isActive: row.isActive})}
                                        disabled={mutating}
                                        className={rowActionClass}
                                      >
                                        Cancel now
                                      </button>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Cway trials have no Stripe subscription by design and are never orphans — end those with Stop trial above. Stripe-backed
                        subscriptions are cancelled in Stripe: at period end (the customer keeps access until then) or immediately (no final invoice,
                        no proration).
                      </p>
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
